import React, { useEffect, useMemo, Component } from 'react';
/**
 * SessionSummary — End-of-session results screen with mastery growth.
 *
 * DDD co-locatie: moved from components/practice/ in s65.
 * Optimized: confetti data, types, copy helpers, useCountUp → sessionSummaryData.ts (~80r).
 */
import { motion } from 'framer-motion';
import { Star, RotateCcw, CheckCircle2, Sparkles, X, Zap, Clock, TrendingUp } from 'lucide-react';
import { type ModuleId, type Question, getFamilyDefinition } from '../../content';
import type { FamilyMasteryState } from '../../mastery';
import { getFamilyMasteryKey, getGrowthStage } from '../../mastery';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { useAppStore } from '../../app/hooks/useAppStore';
import { dataLayer, type PersonalRecords } from '../../data/dataLayer';
import { DjayAvatar, type DjayEmotion, useDjaySelection, getDjaySummaryReaction } from '../../mascot';
import { type SessionResult, CONFETTI, getSummaryHeading, useCountUp } from './sessionSummaryData';
// Re-export for consumers that import the type from here
export type { SessionResult };
// ── Props ────────────────────────────────────────────────────
interface SessionSummaryProps {
  moduleId: ModuleId;
  sessionQuestions: Question[];
  sessionAnswers: SessionResult[];
  preFamilyMastery: Record<string, FamilyMasteryState>;
  isFluency?: boolean;
  labMode?: string;
  onExit: () => void;
  onPracticeAgain?: () => void;
  xpGained: number;
}
// ── Component ────────────────────────────────────────────────
export function SessionSummary({
  moduleId,
  sessionQuestions,
  sessionAnswers,
  preFamilyMastery,
  isFluency = false,
  labMode,
  onExit,
  onPracticeAgain,
  xpGained
}: SessionSummaryProps) {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const currentFamilyMastery = state.familyMastery || {};
  const correctCount = sessionAnswers.filter(a => a.isCorrect).length;
  const totalQuestions = sessionAnswers.length;
  const percentage = totalQuestions > 0 ? Math.round(correctCount / totalQuestions * 100) : 0;
  const isGreat = percentage >= 80;
  // Calculate average speed
  const averageSpeedMs = useMemo(() => {
    if (sessionAnswers.length === 0) return 0;
    const total = sessionAnswers.reduce((sum, a) => sum + (a.timeMs || 0), 0);
    return total / sessionAnswers.length;
  }, [sessionAnswers]);
  const averageSpeedSec = (averageSpeedMs / 1000).toFixed(1);
  // Calculate fluency stats
  const fluencyStats = useMemo(() => {
    if (!isFluency || sessionAnswers.length === 0) return null;
    // Fastest correct answer
    const correctAnswers = sessionAnswers.filter(a => a.isCorrect && a.timeMs);
    let fastestAnswer = null;
    if (correctAnswers.length > 0) {
      fastestAnswer = correctAnswers.reduce((min, curr) => curr.timeMs! < min.timeMs! ? curr : min);
    }
    // Longest streak
    let currentStreak = 0;
    let longestStreak = 0;
    sessionAnswers.forEach(a => {
      if (a.isCorrect) {
        currentStreak++;
        if (currentStreak > longestStreak) longestStreak = currentStreak;
      } else {
        currentStreak = 0;
      }
    });
    return {
      fastestAnswer,
      longestStreak
    };
  }, [isFluency, sessionAnswers]);
  // Djay emotion + speech from registry (single source of truth)
  const summaryReaction = useMemo(() => {
    if (isFluency && averageSpeedMs > 0) {
      const isFast = averageSpeedMs < 4000;
      const isMedium = averageSpeedMs < 8000;
      return {
        emotion: isFast ? 'celebrating' : isMedium ? 'happy' : 'neutral',
        text: isFast ? 'Wat ben jij razendsnel!' : isMedium ? 'Lekker vlot gewerkt!' : 'Goed gefocust!'
      } as {
        emotion: DjayEmotion;
        text: string;
      };
    }
    return getDjaySummaryReaction(percentage);
  }, [percentage, isFluency, averageSpeedMs]);
  const djayEmotion: DjayEmotion = summaryReaction.emotion;
  // Animated score counter
  const animatedCorrect = useCountUp(correctCount, 1.2, 0.5);
  // Heading copy
  const baseHeading = getSummaryHeading(percentage);
  const heading = isFluency ? 'Snel & Zeker' : baseHeading.heading;
  const subtitle = isFluency ? 'Je bent weer een stukje sneller geworden!' : baseHeading.subtitle;
  // Practiced families (max 4)
  const practicedFamilyIds = Array.from(new Set(sessionAnswers.map(q => q.familyId).filter((id): id is string => !!id))).slice(0, 4);
  useEffect(() => {
    if (labMode) {
      dataLayer.saveLastOefenlabSession({
        modeId: labMode,
        familyId: practicedFamilyIds[0],
        timestamp: Date.now(),
        score: percentage
      });
      // Check for newly unlocked challenges
      for (const familyId of practicedFamilyIds) {
        const masteryKey = getFamilyMasteryKey(familyId, selectedGroup);
        const pre = preFamilyMastery[masteryKey]?.masteryScore || 0;
        const post = currentFamilyMastery[masteryKey]?.masteryScore || 0;
        if (pre < 70 && post >= 70) {
          dataLayer.setNewlyUnlockedChallenge(familyId);
          break; // Just store one for now to avoid multiple notifications
        }
      }
    }
  }, [labMode, practicedFamilyIds, percentage, preFamilyMastery, currentFamilyMastery, selectedGroup]);
  // F-9: Update personal records
  useEffect(() => {
    if (!labMode) return;
    dataLayer.getPersonalRecords().then(records => {
      const newRecords: PersonalRecords = {
        ...records
      };
      if (labMode === 'fluency') {
        const currentFluency = newRecords.fluency || {
          fastestAvgMs: Infinity,
          longestStreak: 0,
          bestScore: 0
        };
        newRecords.fluency = {
          fastestAvgMs: averageSpeedMs > 0 ? Math.min(currentFluency.fastestAvgMs, averageSpeedMs) : currentFluency.fastestAvgMs,
          longestStreak: Math.max(currentFluency.longestStreak, fluencyStats?.longestStreak || 0),
          bestScore: Math.max(currentFluency.bestScore, percentage)
        };
      } else if (labMode === 'sprint') {
        const currentSprint = newRecords.sprint || {
          bestScore: 0,
          totalSessions: 0
        };
        newRecords.sprint = {
          bestScore: Math.max(currentSprint.bestScore, percentage),
          totalSessions: currentSprint.totalSessions + 1
        };
      } else if (labMode === 'challenge') {
        const currentChallenge = newRecords.challenge || {
          highestTier: 0,
          bestScore: 0,
          totalCompleted: 0
        };
        // We don't have tier info easily available here, so we just update score and count
        newRecords.challenge = {
          ...currentChallenge,
          bestScore: Math.max(currentChallenge.bestScore, percentage),
          totalCompleted: currentChallenge.totalCompleted + 1
        };
      }
      dataLayer.savePersonalRecords(newRecords);
    });
  }, [labMode, averageSpeedMs, fluencyStats, percentage]);
  return <div className={`min-h-screen bg-gradient-to-b ${isFluency ? 'from-amber-50' : 'from-slate-50'} to-white p-4 md:p-8 flex items-center justify-center`} data-id="element-7368">
      <div className="max-w-lg w-full" data-id="element-7369">
        <Card className={`p-6 md:p-8 shadow-2xl ${isFluency ? 'shadow-amber-100/60 border-amber-100' : 'shadow-indigo-100/60 border-slate-100'} bg-white rounded-3xl relative overflow-hidden`} data-id="element-7370">
          {/* X button — top right */}
          <button onClick={onExit} className="absolute top-4 right-4 z-30 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Sluiten" data-id="element-7371">
            <X className="w-5 h-5" data-id="element-7372" />
          </button>

          {/* Confetti particles for high scores */}
          {isGreat && CONFETTI.map((dot, i) => <motion.div key={i} className={`absolute rounded-full ${dot.color} ${dot.size} pointer-events-none`} style={{
          top: dot.top,
          left: dot.left,
          right: dot.right
        }} initial={{
          opacity: 0,
          scale: 0,
          y: 0
        }} animate={{
          opacity: [0, 1, 1, 0],
          scale: [0, 1.3, 1, 0.7],
          y: [0, -12, -20, -30]
        }} transition={{
          delay: dot.delay,
          duration: 2.5,
          ease: 'easeOut'
        }} data-id="element-7373" />)}

          {/* ── COMPACT HEADER ── */}
          <div className="text-center mb-6 relative z-10 pt-2" data-id="element-7374">
            {/* @djay-slot: summary-header */}
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            bounce: 0.5,
            delay: 0.1
          }} className="flex items-center justify-center mx-auto mb-4" data-id="element-7375">
              <DjayAvatar size="xl" emotion={djayEmotion} variant={selectedVariant} data-id="element-7376" />
            </motion.div>

            {/* Djay speech bubble */}
            <motion.div initial={{
            opacity: 0,
            y: 6,
            scale: 0.92
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            delay: 0.25,
            duration: 0.3,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative mb-4" data-id="element-7377">
              <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 border-t border-l border-slate-200/70 z-10" data-id="element-7378" />
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-md shadow-slate-200/50 border border-slate-200/70 px-5 py-2.5 text-center max-w-[280px] mx-auto" data-id="element-7379">
                <p className="font-bold text-slate-700 text-sm leading-snug" data-id="element-7380">
                  {summaryReaction.text}
                </p>
              </div>
            </motion.div>

            {/* Heading */}
            <motion.h2 initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3,
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className={`text-2xl md:text-3xl font-black ${isFluency ? 'text-amber-900' : 'text-slate-900'} mb-1 tracking-tight`} data-id="element-7381">
              {heading}
            </motion.h2>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4
          }} className={`text-base font-medium ${isFluency ? 'text-amber-700/80' : 'text-slate-500'} mb-4`} data-id="element-7382">
              {subtitle}
            </motion.p>

            {/* Score — clean inline stats */}
            <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5,
            duration: 0.4
          }} className="flex items-center justify-center gap-2 flex-wrap" data-id="element-7383">
              {isFluency && averageSpeedMs > 0 ? <>
                  <div className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-full border border-amber-200" data-id="element-7384">
                    <Zap className="w-4 h-4 text-amber-500 fill-amber-500 flex-shrink-0" data-id="element-7385" />
                    <span className="font-bold text-amber-800 text-sm" data-id="element-7386">
                      {averageSpeedSec}s gemiddeld
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-full border border-slate-100" data-id="element-7387">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-7388" />
                    <span className="font-bold text-slate-700 text-sm" data-id="element-7389">
                      <motion.span data-id="element-7390">{animatedCorrect}</motion.span>/
                      {totalQuestions} goed
                    </span>
                  </div>
                </> : <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-full border border-slate-100" data-id="element-7391">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-7392" />
                  <span className="font-bold text-slate-700 text-sm" data-id="element-7393">
                    <motion.span data-id="element-7394">{animatedCorrect}</motion.span> van de{' '}
                    {totalQuestions} goed
                  </span>
                </div>}
            </motion.div>
          </div>

          {/* ── MASTERY DELTA ── */}
          {!isFluency && practicedFamilyIds.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-6" data-id="element-7395">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5 px-1" data-id="element-7396">
                <Sparkles className="w-4 h-4 text-indigo-400" data-id="element-7397" />
                Wat heb je geoefend?
              </h3>
              <div className="space-y-2" data-id="element-7398">
                {practicedFamilyIds.map((familyId, index) => {
              const def = getFamilyDefinition(familyId);
              const masteryKey = getFamilyMasteryKey(familyId, selectedGroup);
              const pre = preFamilyMastery[masteryKey]?.masteryScore || 0;
              const post = currentFamilyMastery[masteryKey]?.masteryScore || 0;
              const delta = post - pre;
              if (!def) return null;
              const preStage = getGrowthStage(pre);
              const postStage = getGrowthStage(post);
              // Real level up: only count transitions ABOVE level 1
              const leveledUp = postStage.level > preStage.level && preStage.level >= 1;
              return <motion.div key={familyId} initial={{
                opacity: 0,
                x: -8
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.7 + index * 0.08,
                duration: 0.35
              }} className="bg-slate-50 rounded-xl p-3 border border-slate-100 flex items-center gap-3" data-id="element-7399">
                      <div className="text-2xl flex-shrink-0" data-id="element-7400">
                        {postStage.emoji}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-7401">
                        <div className="font-semibold text-slate-800 text-sm truncate" data-id="element-7402">
                          {def.description}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-7403">
                          {postStage.label}
                        </div>
                      </div>
                      <div className="flex-shrink-0" data-id="element-7404">
                        {leveledUp ? <motion.span initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    delay: 0.9 + index * 0.08
                  }} className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-100" data-id="element-7405">
                            <Sparkles className="w-3 h-3" data-id="element-7406" /> Level up!
                          </motion.span> : delta > 0 ? <span className="text-xs font-semibold text-indigo-500" data-id="element-7407">
                            {Math.round(pre)}% → {Math.round(post)}%
                          </span> : <CheckCircle2 className="w-4 h-4 text-slate-300" data-id="element-7408" />}
                      </div>
                    </motion.div>;
            })}
              </div>
            </motion.div>}

          {/* ── FLUENCY STATS ── */}
          {isFluency && fluencyStats && <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.75,
          duration: 0.5
        }} className="mb-6" data-id="element-7409">
              <h3 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-3 flex items-center gap-1.5 px-1" data-id="element-7410">
                <Zap className="w-4 h-4" data-id="element-7411" />
                Snel & Zeker Resultaten
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-7412">
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-100" data-id="element-7413">
                  <div className="flex items-center gap-2 text-amber-800 mb-1" data-id="element-7414">
                    <TrendingUp className="w-4 h-4" data-id="element-7415" />
                    <span className="text-xs font-bold uppercase" data-id="element-7416">
                      Langste Reeks
                    </span>
                  </div>
                  <div className="text-2xl font-black text-amber-600" data-id="element-7417">
                    {fluencyStats.longestStreak}{' '}
                    <span className="text-sm font-medium text-amber-700/70" data-id="element-7418">
                      goed
                    </span>
                  </div>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-100" data-id="element-7419">
                  <div className="flex items-center gap-2 text-amber-800 mb-1" data-id="element-7420">
                    <Clock className="w-4 h-4" data-id="element-7421" />
                    <span className="text-xs font-bold uppercase" data-id="element-7422">Snelste</span>
                  </div>
                  <div className="text-2xl font-black text-amber-600" data-id="element-7423">
                    {fluencyStats.fastestAnswer ? (fluencyStats.fastestAnswer.timeMs! / 1000).toFixed(1) : '-'}{' '}
                    <span className="text-sm font-medium text-amber-700/70" data-id="element-7424">
                      sec
                    </span>
                  </div>
                </div>
              </div>

              {/* Per-question speed breakdown */}
              <div className="bg-white border border-amber-100 rounded-xl overflow-hidden" data-id="element-7425">
                <div className="bg-amber-50/50 px-3 py-2 border-b border-amber-100 text-xs font-bold text-amber-800" data-id="element-7426">
                  Snelheid per vraag
                </div>
                <div className="max-h-32 overflow-y-auto p-1" data-id="element-7427">
                  {sessionAnswers.map((ans, i) => {
                const q = sessionQuestions.find(q => q.id === ans.questionId);
                const qText = q?.question ? q.question.length > 30 ? q.question.substring(0, 30) + '...' : q.question : `Vraag ${i + 1}`;
                return <div key={i} className="flex items-center justify-between px-2 py-1.5 hover:bg-amber-50/30 rounded-lg text-sm" data-id="element-7428">
                        <div className="flex items-center gap-2 truncate pr-2" data-id="element-7429">
                          {ans.isCorrect ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" data-id="element-7430" /> : <X className="w-3.5 h-3.5 text-rose-400 flex-shrink-0" data-id="element-7431" />}
                          <span className="text-slate-600 truncate" data-id="element-7432">
                            {qText}
                          </span>
                        </div>
                        <div className={`font-mono font-medium flex-shrink-0 ${ans.timeMs && ans.timeMs <= 8000 ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-7433">
                          {ans.timeMs ? (ans.timeMs / 1000).toFixed(1) + 's' : '-'}
                        </div>
                      </div>;
              })}
                </div>
              </div>
            </motion.div>}

          {/* ── ENCOURAGEMENT ── */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.85,
          duration: 0.4
        }} className="mb-6" data-id="element-7434">
            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 text-center" data-id="element-7435">
              <div className="text-2xl mb-1" data-id="element-7436">🚀</div>
              <div className="font-bold text-indigo-900 text-sm" data-id="element-7437">
                Ga zo door!
              </div>
              <div className="text-xs text-indigo-600" data-id="element-7438">
                Elke keer word je beter!
              </div>
            </div>
          </motion.div>

          {/* ── ACTION BUTTONS ── */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.0,
          duration: 0.4
        }} className="relative z-20 space-y-2.5" data-id="element-7439">
            {onPracticeAgain && <Button onClick={onPracticeAgain} size="xl" variant="primary" fullWidth className="shadow-lg shadow-indigo-200/50 h-14 text-base rounded-2xl" data-id="element-7440">
                <RotateCcw className="w-5 h-5 mr-2" data-id="element-7441" />
                Nog een keer oefenen
              </Button>}
            <Button onClick={onExit} size="xl" variant={onPracticeAgain ? 'secondary' : 'primary'} fullWidth className={`h-14 text-base rounded-2xl ${onPracticeAgain ? '' : 'shadow-lg shadow-indigo-200/50'}`} data-id="element-7442">
              {onPracticeAgain ? 'Probeer een andere modus' : 'Terug naar Oefenlab'}
            </Button>
          </motion.div>
        </Card>
      </div>
    </div>;
}